import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import {
  borrowerIntakeDocuments,
  getRequiredBorrowerDocuments,
  getConditionalBorrowerDocuments
} from '../../config/intakeDocumentRequirements';

const BorrowerIntakeDocuments = ({ currentUser, loanApplicationId, personalGuarantors = [], businessId }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [conditions, setConditions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing uploaded intake documents
  useEffect(() => {
    const fetchUploadedDocs = async () => {
      if (!currentUser?.uid || !loanApplicationId) {
        setIsLoading(false);
        return;
      }

      try {
        // Query for documents and filter for intake type client-side
        const q = query(
          collection(db, 'documentMetadata'),
          where('userId', '==', currentUser.uid),
          where('loanApplicationId', '==', loanApplicationId)
        );
        const querySnapshot = await getDocs(q);
        const docs = querySnapshot.docs
          .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
          .filter(doc => doc.documentType === 'intake');
        setUploadedDocs(docs);
        setIsLoading(false);

        // Check and update completion status on initial load
        if (docs.length > 0) {
          checkAndUpdateIntakeCompletion(docs);
        }
      } catch (error) {
        console.error('Error fetching intake documents:', error);
        setIsLoading(false);
      }
    };

    fetchUploadedDocs();
  }, [currentUser?.uid, loanApplicationId]);

  useEffect(() => {
    if (!loanApplicationId) return;
    getDoc(doc(db, 'loanApplications', loanApplicationId)).then(snapshot => {
      setConditions(snapshot.data()?.packagingDocumentConditions || {});
    }).catch(error => console.error('Error loading packaging conditions:', error));
  }, [loanApplicationId]);

  const handleConditionChange = (conditionKey) => {
    setConditions(prev => {
      const next = { ...prev, [conditionKey]: !prev[conditionKey] };
      if (loanApplicationId) {
        updateDoc(doc(db, 'loanApplications', loanApplicationId), { packagingDocumentConditions: next })
          .catch(error => console.error('Error saving packaging conditions:', error));
      }
      return next;
    });
  };

  const isDocumentUploaded = (documentName, label, guarantorId = null) => {
    return uploadedDocs.some(doc => {
      const baseMatch = doc.documentName === documentName &&
        doc.label === label &&
        doc.documentType === 'intake';

      if (guarantorId) {
        return baseMatch && (doc.ownerId === guarantorId || doc.guarantorId === guarantorId);
      }
      return baseMatch && !doc.ownerId && !doc.guarantorId;
    });
  };

  const getUploadedDoc = (documentName, label, guarantorId = null) => {
    return uploadedDocs.find(doc => {
      const baseMatch = doc.documentName === documentName &&
        doc.label === label &&
        doc.documentType === 'intake';

      if (guarantorId) {
        return baseMatch && (doc.ownerId === guarantorId || doc.guarantorId === guarantorId);
      }
      return baseMatch && !doc.ownerId && !doc.guarantorId;
    });
  };

  const handleFileUpload = async (event, documentName, label, guarantorId = null, guarantorName = null) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!currentUser?.uid || !loanApplicationId) {
      alert('Missing user or loan application ID. Please try again.');
      return;
    }

    const uploadKey = guarantorId
      ? `${documentName}-${label}-${guarantorId}`
      : `${documentName}-${label}`;
    setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'uploading' }));

    try {
      // Check if a file already exists for this document and label
      const existingDoc = getUploadedDoc(documentName, label, guarantorId);

      // If a file exists, delete it from storage and Firestore
      if (existingDoc) {
        try {
          const oldStorageRef = ref(storage, existingDoc.fileUrl);
          await deleteObject(oldStorageRef);
        } catch (e) {
          console.log('Could not delete old file from storage:', e);
        }
        await deleteDoc(doc(db, 'documentMetadata', existingDoc.id));
      }

      // Build storage path
      const guarantorPath = guarantorId ? `/${guarantorId}` : '';
      const storagePath = `documents/${currentUser.uid}/${loanApplicationId}/intake/${documentName}/${label}${guarantorPath}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      const fileMetadata = {
        documentName,
        label,
        fileName: file.name,
        fileUrl,
        uploadedAt: new Date().toISOString(),
        userId: currentUser.uid,
        loanApplicationId,
        documentType: 'intake',
        status: 'uploaded',
        ...(guarantorId && { guarantorId }),
        ...(guarantorId && { ownerId: guarantorId }),
        ...(guarantorName && { guarantorName }),
        ...(businessId && { businessId })
      };

      const docRef = await addDoc(collection(db, 'documentMetadata'), fileMetadata);

      // Update local state and check completion
      setUploadedDocs(prev => {
        const filtered = prev.filter(d => {
          const baseMatch = d.documentName === documentName && d.label === label;
          if (guarantorId) {
            return !(baseMatch && (d.ownerId === guarantorId || d.guarantorId === guarantorId));
          }
          return !(baseMatch && !d.ownerId && !d.guarantorId);
        });
        const updated = [...filtered, { id: docRef.id, ...fileMetadata }];

        // Check if all required intake documents are now uploaded
        checkAndUpdateIntakeCompletion(updated);

        return updated;
      });

      setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'completed' }));

      // Reset status after a delay
      setTimeout(() => {
        setUploadingStatus(prev => ({ ...prev, [uploadKey]: null }));
      }, 2000);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'error' }));
      alert('Error uploading file. Please try again.');
    }
  };

  // Check if all required intake documents are uploaded and update loanApplication
  const checkAndUpdateIntakeCompletion = async (currentUploadedDocs) => {
    if (!loanApplicationId) return;

    const requiredDocuments = getRequiredBorrowerDocuments();
    let allRequiredUploaded = true;

    for (const document of requiredDocuments) {
      if (document.individual) {
        // Individual documents need to be uploaded for each guarantor
        if (personalGuarantors.length === 0) {
          allRequiredUploaded = false;
          break;
        }
        for (const guarantor of personalGuarantors) {
          const guarantorId = guarantor.id || `${guarantor.firstName} ${guarantor.lastName}`;
          for (const label of document.labels) {
            const isUploaded = currentUploadedDocs.some(d =>
              d.documentName === document.name &&
              d.label === label &&
              d.documentType === 'intake' &&
              (d.ownerId === guarantorId || d.guarantorId === guarantorId)
            );
            if (!isUploaded) {
              allRequiredUploaded = false;
              break;
            }
          }
          if (!allRequiredUploaded) break;
        }
      } else {
        // Shared documents need at least one label uploaded
        const hasAnyLabel = document.labels.some(label =>
          currentUploadedDocs.some(d =>
            d.documentName === document.name &&
            d.label === label &&
            d.documentType === 'intake' &&
            !d.ownerId && !d.guarantorId
          )
        );
        if (!hasAnyLabel) {
          allRequiredUploaded = false;
          break;
        }
      }
      if (!allRequiredUploaded) break;
    }

    // Update the loanApplication document with completion status
    try {
      const loanRef = doc(db, 'loanApplications', loanApplicationId);
      await updateDoc(loanRef, {
        borrowerIntakeComplete: allRequiredUploaded,
        borrowerIntakeLastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating intake completion status:', error);
    }
  };

  const requiredDocs = getRequiredBorrowerDocuments();
  const conditionalDocs = getConditionalBorrowerDocuments();

  // Get applicable conditional docs based on selected conditions
  const applicableConditionalDocs = conditionalDocs.filter(
    doc => conditions[doc.conditionKey]
  );

  const renderDocumentUploadSlot = (document, label, guarantorId = null, guarantorName = null, index) => {
    const uploadKey = guarantorId
      ? `${document.name}-${label}-${guarantorId}`
      : `${document.name}-${label}`;
    const isUploaded = isDocumentUploaded(document.name, label, guarantorId);
    const uploadedDoc = getUploadedDoc(document.name, label, guarantorId);
    const status = uploadingStatus[uploadKey];
    const inputId = `borrower-intake-${document.name.replace(/\s+/g, '-')}-${label.replace(/\s+/g, '-')}-${guarantorId || 'shared'}-${index}`;

    return (
      <div key={uploadKey} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isUploaded ? (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
          ) : (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
              <DocumentIcon className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {label}
              {guarantorName && (
                <span className="ml-2 text-xs text-indigo-600">({guarantorName})</span>
              )}
            </p>
            {isUploaded && uploadedDoc && (
              <p className="text-xs text-gray-500 truncate">{uploadedDoc.fileName}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 ml-3">
          {status === 'uploading' ? (
            <div className="flex items-center gap-2 text-indigo-600">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm">Uploading...</span>
            </div>
          ) : status === 'completed' ? (
            <span className="text-sm text-green-600 font-medium">Uploaded!</span>
          ) : status === 'error' ? (
            <span className="text-sm text-red-600 font-medium">Error</span>
          ) : (
            <label
              htmlFor={inputId}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                isUploaded
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              {isUploaded ? 'Replace' : 'Upload'}
              <input
                id={inputId}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => handleFileUpload(e, document.name, label, guarantorId, guarantorName)}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  const renderIndividualDocumentSection = (document, index) => {
    // Individual documents need to be uploaded per guarantor
    return (
      <div key={document.name} className="mb-6">
        <div className="flex items-start gap-2 mb-2">
          <h4 className="text-sm font-semibold text-gray-800">{document.name}</h4>
          {document.required && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              Required
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">{document.description}</p>
        {document.downloadUrl && (
          <div className="mb-3">
            <a
              href={document.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
            >
              <DocumentIcon className="w-4 h-4" />
              {document.downloadLabel || 'Download Form'}
            </a>
          </div>
        )}
        {document.validationNote && (
          <div className="flex items-start gap-2 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{document.validationNote}</p>
          </div>
        )}

        {/* Per-guarantor upload slots */}
        {personalGuarantors.length > 0 ? (
          <div className="space-y-4">
            {personalGuarantors.map((guarantor, gIndex) => {
              const guarantorName = `${guarantor.firstName} ${guarantor.lastName}`;
              const guarantorId = guarantor.id || guarantorName;

              return (
                <div key={guarantorId} className="pl-4 border-l-2 border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCircleIcon className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-indigo-700">{guarantorName}</span>
                  </div>
                  <div className="space-y-2">
                    {document.labels.map((label, labelIndex) =>
                      renderDocumentUploadSlot(document, label, guarantorId, guarantorName, `${index}-${gIndex}-${labelIndex}`)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm text-gray-500">No guarantors found. Please add guarantors first.</p>
          </div>
        )}
      </div>
    );
  };

  const renderSharedDocumentSection = (document, index) => {
    // Shared documents are uploaded once for the whole application
    return (
      <div key={document.name} className="mb-6">
        <div className="flex items-start gap-2 mb-2">
          <h4 className="text-sm font-semibold text-gray-800">{document.name}</h4>
          {document.required && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              Required
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">{document.description}</p>
        {document.downloadUrl && (
          <div className="mb-3">
            <a
              href={document.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
            >
              <DocumentIcon className="w-4 h-4" />
              {document.downloadLabel || 'Download Form'}
            </a>
          </div>
        )}
        {document.validationNote && (
          <div className="flex items-start gap-2 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{document.validationNote}</p>
          </div>
        )}
        <div className="space-y-2">
          {document.labels.map((label, labelIndex) =>
            renderDocumentUploadSlot(document, label, null, null, `${index}-${labelIndex}`)
          )}
        </div>
      </div>
    );
  };

  const renderDocumentSection = (document, index) => {
    if (document.individual) {
      return renderIndividualDocumentSection(document, index);
    }
    return renderSharedDocumentSection(document, index);
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-5 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-xl overflow-hidden border border-emerald-100">
      {/* Header */}
      <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex items-center">
          <DocumentIcon className="h-5 w-5 text-emerald-600 mr-2" />
          <h3 className="text-lg font-semibold leading-7 text-gray-900">Capital Packaging Documents</h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
        >
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-emerald-100 px-6 py-5">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200 mb-6">
            <InformationCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-800">
                Your engagement is active. Upload the remaining documents so we can prepare your lender package and, if selected, your investor materials before you have a deal under LOI.
              </p>
            </div>
          </div>

          {/* Required Documents Section */}
          <div className="mb-8">
            <h3 className="text-md font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Required After Engagement
            </h3>
            {requiredDocs.map((doc, index) => renderDocumentSection(doc, index))}
          </div>

          {/* Conditional Documents Section */}
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Documents Based on Your Situation
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Check the boxes below if any of these situations apply to you:
            </p>

            {/* Checkboxes for conditions */}
            <div className="space-y-3 mb-6">
              {conditionalDocs.map((doc) => (
                <label
                  key={doc.conditionKey}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={conditions[doc.conditionKey] || false}
                    onChange={() => handleConditionChange(doc.conditionKey)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{doc.conditionLabel}</span>
                </label>
              ))}
            </div>

            {/* Conditional Document Upload Slots */}
            {applicableConditionalDocs.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4">
                  Additional Documents Required
                </h4>
                {applicableConditionalDocs.map((doc, index) => renderDocumentSection(doc, `conditional-${index}`))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowerIntakeDocuments;
